import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useUserAccess from "../../../Hooks/useUserAccess";
import useModal from "../../../Hooks/useModal";
import * as formValidationRules from '../../../Constants/formValidationRules'
import formActions from "../../../Actions/formActions";
import subscriptionActions from "../../../Actions/Voss/subscriptionActions";
import customerActions from "../../../Actions/customerActions";
import { useEffect, useState } from "react";
import Modals from "../../../Constants/modals";
import { useStep } from "../../../Hooks/useStep";
import { UserRoles } from "../../../Constants/userRoles";
import { Button, ButtonDirection, ButtonOption } from "../../Fundamentals/Button";
import { StepperModal } from "../../Fundamentals/Modal/StepperModal";
import { ModalSize } from "../../Fundamentals/Modal/StandardModal";
import { StandardStepFooter, Step, StepBody, StepFooter, StepHeaderType } from "../../Fundamentals/Stepper/Step";
import SubscriptionTerminationForm from "../../Customer/VOSS/Subscriptions/SubscriptionTerminationForm";
import SubscriptionTerminationEmailTemplate from "../../Customer/VOSS/Subscriptions/SubscriptionTerminationEmailTemplate";
import forms from "../../../Constants/forms";
import { vossSubscriptionItemTerminationTiming } from "../../../Constants/VOSS/vossSubscriptionItemTerminationTiming";
import { useNavigate } from "react-router-dom";
import customerRoutes from "../../../Constants/Customer/customerRoutes";

const SubscriptionTerminationModal = ({ subscription }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { hasAdminAccess } = useUserAccess();
    const { closeModal } = useModal();
    const customer = useSelector(state => state.CustomerReducer.customer);
    const dataLoading = useSelector(state => state.CommonReducer.dataLoading.terminateSubscription || state.CommonReducer.dataLoading.updateEmailTemplate);
    const form = useSelector(state => state.FormReducer.forms[forms.cancelVossSubscription]);
    const [warrantyEligibility, setWarrantyEligibility] = useState(false);

    useEffect(() => {
        dispatch(customerActions.getVossTerminationEmailTemplate(subscription));
        dispatch(subscriptionActions.getSubscriptionWarrantyEligibility(subscription.id, setWarrantyEligibility));

        return () => {
            dispatch(formActions.cancelFormEditing(forms.cancelVossSubscription));
        };
    }, []);

    useEffect(() => {
        if (form) {
            if (form.data.useWarranty) {
                dispatch(formActions.updateFormValidationRules(forms.cancelVossSubscription, null));
            } else {
                dispatch(formActions.updateFormValidationRules(forms.cancelVossSubscription, formValidationRules.CancelSubscription));
            }
        }
    }, [form?.data.useWarranty]);

    const onSubmit = (callback) => {
        dispatch(formActions.submitForm(form, callback));
    }

    const updateTerminationEmail = (callback) => {
        onSubmit(() => dispatch(customerActions.updateVossTerminationEmailTemplate(form, subscription, callback)));
    }

    const onSave = (notifyUser) => {
        onSubmit(() => dispatch(customerActions.cancelSubscription(form, subscription, notifyUser, onCloseClick)));
    }

    const onCloseClick = (termination) => {
        if (termination.timing == vossSubscriptionItemTerminationTiming.Immediately) {
            navigate(customerRoutes.customer(customer.pathId))
            dispatch(customerActions.searchSubscriptions(customer.vossCustomer.id));
        } else {
            dispatch(customerActions.searchSubscriptions(customer.vossCustomer.id));
            dispatch(subscriptionActions.getSubscription(subscription.id));
        }

        closeModal(Modals.SubscriptionTermination);
    }

    const onChange = (field, value) => {
        dispatch(formActions.changeMultiFormField(form, field, value));
    }

    const TerminationFormFooter = () => {
        const { onNext } = useStep();
        return <div className="w-100 d-flex justify-content-end gap-8">
            {hasAdminAccess(UserRoles.SubscriptionAdmin) ? (
                <>
                    <Button onClick={() => onSave(false)}>{t('subscriptiontermination.terminatewithoutnotifying')}</Button>
                    <Button onClick={() => updateTerminationEmail(onNext)} direction={ButtonDirection.RIGHT} option={ButtonOption.PRIMARY}>{t('subscriptiontermination.terminateandnotify')}</Button>
                </>
            ) : (
                <Button onClick={() => updateTerminationEmail(onNext)} direction={ButtonDirection.RIGHT} option={ButtonOption.PRIMARY}>Continue</Button>
            )}
            <Button onClick={onCloseClick}>Cancel</Button>
        </div>
    }

    return <StepperModal
        isLoading={!form?.data}
        isStepLoading={dataLoading}
        title={t("subscriptiontermination.terminationof", { product: subscription.tier.name })}
        onClose={onCloseClick}
        modalSize={ModalSize.LARGE}
        stepHeaderType={StepHeaderType.NONE}>
        <Step>
            <StepBody>
                <SubscriptionTerminationForm form={form} subscription={subscription} onChange={onChange} warrantyEligibility={warrantyEligibility} />
            </StepBody>
            <StepFooter>
                <TerminationFormFooter />
            </StepFooter>
        </Step>
        <Step>
            <StepBody>
                <SubscriptionTerminationEmailTemplate form={form} onChange={onChange} />
            </StepBody>
            <StandardStepFooter onCancel={onCloseClick}>
                <Button onClick={() => onSave(true, onCloseClick)} option={ButtonOption.PRIMARY}>{t('subscriptiontermination.terminateandnotify')}</Button>
            </StandardStepFooter>
        </Step>
    </StepperModal>
}

export default SubscriptionTerminationModal;